import React, { useEffect, useState } from "react";
import { useLang } from "../context/LangContext";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";

const TYPES = [{ v: "all", k: "allTypes" }, { v: "apartment", k: "apartment" }, { v: "house", k: "house" }, { v: "land", k: "land" }];

export default function FiltersSidebar({ filters, onChange, onReset }) {
  const { t } = useLang();
  const [tree, setTree] = useState({});
  const [local, setLocal] = useState(filters);
  useEffect(() => setLocal(filters), [filters]);
  useEffect(() => { api.get("/locations").then((r) => setTree(r.data.tree)); }, []);

  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const setBool = (k) => set(k, local[k] ? "" : "true");
  const cities = local.wilaya && local.wilaya !== "all" ? (tree[local.wilaya] || []) : [];

  return (
    <aside className="w-full lg:w-80 bg-white border border-slate-200/70 rounded-2xl p-6 h-fit lg:sticky lg:top-24" data-testid="filters-sidebar">
      <h3 className="text-base font-bold text-[#111827] mb-5">{t("filters")}</h3>

      <div className="space-y-5">
        <Section label={t("transaction")}>
          <div className="flex rounded-xl bg-slate-50 p-1">
            {["sale", "rent"].map((tx) => (
              <button key={tx} type="button" onClick={() => set("transaction", tx)} className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition ${local.transaction === tx ? "bg-white text-[#1B8F5A] shadow-sm" : "text-slate-500"}`}>
                {tx === "sale" ? t("buy") : t("rent")}
              </button>
            ))}
          </div>
        </Section>

        <Section label={t("propertyType")}>
          <div className="flex flex-wrap gap-2">
            {TYPES.map(({ v, k }) => (
              <button key={v} type="button" onClick={() => set("type", v)} className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${local.type === v ? "bg-[#1B8F5A] text-white border-[#1B8F5A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#1B8F5A]"}`}>
                {t(k)}
              </button>
            ))}
          </div>
        </Section>

        <Section label={t("location")}>
          <Select value={local.wilaya || "all"} onValueChange={(v) => setLocal({ ...local, wilaya: v, city: "" })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allWilayas")}</SelectItem>{Object.keys(tree).map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
          </Select>
          {cities.length > 0 && (
            <Select value={local.city || "all"} onValueChange={(v) => set("city", v === "all" ? "" : v)}>
              <SelectTrigger className="mt-2"><SelectValue placeholder={t("city")} /></SelectTrigger>
              <SelectContent><SelectItem value="all">— {t("city")}</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </Section>

        <Section label={`${t("minPrice")} / ${t("maxPrice")} (DA)`}>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" value={local.min_price || ""} onChange={(e) => set("min_price", e.target.value)} placeholder="0" />
            <Input type="number" value={local.max_price || ""} onChange={(e) => set("max_price", e.target.value)} placeholder="∞" />
          </div>
        </Section>

        <Section label={`${t("minSurface")} / ${t("maxSurface")} m²`}>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" value={local.min_surface || ""} onChange={(e) => set("min_surface", e.target.value)} placeholder="0" />
            <Input type="number" value={local.max_surface || ""} onChange={(e) => set("max_surface", e.target.value)} placeholder="∞" />
          </div>
        </Section>

        <Section label={t("rooms")}>
          <div className="flex gap-2">
            {["", "1", "2", "3", "4"].map((r) => (
              <button key={r} type="button" onClick={() => set("rooms", r)} className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg border ${(local.rooms || "") === r ? "bg-[#1B8F5A] text-white border-[#1B8F5A]" : "bg-white text-slate-600 border-slate-200"}`}>
                {r === "" ? "—" : r === "4" ? "4+" : r}
              </button>
            ))}
          </div>
        </Section>

        <Section label={t("features")}>
          <div className="space-y-2">
            {[["furnished", "furnished"], ["balcony", "balcony"], ["parking", "parking"], ["title_deed", "titleDeed"], ["land_registry", "landRegistry"]].map(([k, lab]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={!!local[k]} onCheckedChange={() => setBool(k)} data-testid={`filter-${k}`} />
                <span>{t(lab)}</span>
              </label>
            ))}
          </div>
        </Section>

        <Section label={t("condition")}>
          <div className="flex gap-2">
            {[["", "—"], ["new", t("new")], ["old", t("old")]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("condition", v)} className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg border ${(local.condition || "") === v ? "bg-[#1B8F5A] text-white border-[#1B8F5A]" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
            ))}
          </div>
        </Section>

        <Section label={t("paymentType")}>
          <div className="flex gap-2">
            {[["", "—"], ["cash", t("cash")], ["installment", t("installment")]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("payment_type", v)} className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg border ${(local.payment_type || "") === v ? "bg-[#1B8F5A] text-white border-[#1B8F5A]" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
            ))}
          </div>
        </Section>

        <div className="flex gap-2 pt-3 border-t border-slate-100">
          <Button onClick={() => onChange(local)} className="flex-1 bg-[#1B8F5A] hover:bg-[#157047]" data-testid="filters-apply">{t("apply")}</Button>
          <Button onClick={onReset} variant="outline" data-testid="filters-reset">{t("reset")}</Button>
        </div>
      </div>
    </aside>
  );
}

function Section({ label, children }) {
  return (<div><label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 block">{label}</label>{children}</div>);
}
